import { useState, useEffect, useRef } from 'react';
import { Search, X } from 'lucide-react';

const GIPHY_KEY = 'JEtHABMa9ZZWAWEx4XKypYVtcZuRjDqs';

interface Props {
  onSelect: (url: string) => void;
  onClose: () => void;
}

type Tab = 'gifs' | 'stickers';

export const GifPicker = ({ onSelect, onClose }: Props) => {
  const [tab, setTab] = useState<Tab>('gifs');
  const [query, setQuery] = useState('');
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const debounceRef = useRef<any>(null);

  const fetchItems = async (q: string, t: Tab) => {
    setLoading(true);
    try {
      const type = t === 'gifs' ? 'gifs' : 'stickers';
      const endpoint = q
        ? `https://api.giphy.com/v1/${type}/search?api_key=${GIPHY_KEY}&q=${encodeURIComponent(q)}&limit=15&rating=g`
        : `https://api.giphy.com/v1/${type}/trending?api_key=${GIPHY_KEY}&limit=15&rating=g`;
      const res = await fetch(endpoint);
      const data = await res.json();
      setItems(data.data || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchItems(query, tab); }, [tab]);

  const handleSearch = (val: string) => {
    setQuery(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchItems(val, tab), 500);
  };

  const handleTabChange = (t: Tab) => {
    setTab(t);
    setQuery('');
  };

  return (
    <div className="gif-picker">
      {/* Header */}
      <div className="gif-picker__header">
        <div className="gif-picker__tabs">
          <button className={`gif-picker__tab ${tab === 'gifs' ? 'gif-picker__tab--active' : ''}`} onClick={() => handleTabChange('gifs')}>
            GIF
          </button>
          <button className={`gif-picker__tab ${tab === 'stickers' ? 'gif-picker__tab--active' : ''}`} onClick={() => handleTabChange('stickers')}>
            🎨 Stickers
          </button>
        </div>
        <button className="gif-picker__close" onClick={onClose}><X size={16} /></button>
      </div>

      {/* Search */}
      <div className="gif-picker__search">
        <Search size={15} color="var(--text-dim)" />
        <input
          className="gif-picker__input"
          placeholder={tab === 'gifs' ? 'Buscar GIFs...' : 'Buscar stickers...'}
          value={query}
          onChange={e => handleSearch(e.target.value)}
          autoFocus
        />
      </div>

      {/* Grid */}
      <div className={`gif-picker__grid ${tab === 'stickers' ? 'gif-picker__grid--stickers' : ''}`}>
        {loading && <div className="gif-picker__loading" style={{ gridColumn: '1/-1' }}>Cargando...</div>}
        {!loading && items.length === 0 && <div className="gif-picker__empty" style={{ gridColumn: '1/-1' }}>Sin resultados</div>}
        {!loading && items.map((item: any) => (
          <div
            key={item.id}
            className={`gif-picker__item ${tab === 'stickers' ? 'gif-picker__item--sticker' : ''}`}
            onClick={() => { onSelect(item.images.fixed_height.url); onClose(); }}
          >
            <img
              src={tab === 'stickers' ? item.images.fixed_height_small.url : item.images.fixed_height_small.url}
              alt={item.title}
              loading="lazy"
            />
          </div>
        ))}
      </div>

      {/* Giphy attribution */}
      <div className="gif-picker__attribution">
        Powered by <strong>GIPHY</strong>
      </div>
    </div>
  );
};